<template>
  <div v-if="!readonly" class="mb-4">
    <el-button type="primary" @click="addStore">
      <svg-icon icon="lucide:circle-plus" />新增店铺
    </el-button>
  </div>

  <el-tag v-else-if="isShowTag" class="w-full h-8 justify-start mb-4">
    申请账户信息若有问题可以随时联系运营人员
  </el-tag>

  <el-collapse v-model="activeName" accordion>
    <template v-for="(v, idx) in formValues.stores" :key="idx">
      <el-collapse-item :name="String(idx)">
        <template #title>
          <el-radio-group v-if="!readonly" v-model="selectedIdx" class="ml-6 mr-4">
            <el-radio :value="idx" @click.stop="selectRow(idx)" />
          </el-radio-group>

          <div class="coll-title w-[160px] max-3xl:w-[120px]" :class="{ 'pl-4': readonly }">
            业务类型：<label>{{ yibaoBussinessTypeName[EYibaoBusinessType[v.businessType]] || '——' }}</label>
          </div>
          <div class="coll-title w-[250px]">
            电商平台店铺ID：<label class="w-[120px] text-left">{{ v.shopId || '——' }}</label>
          </div>
          <div class="coll-title w-[360px] max-3xl:w-[280px]">
            电商平台店铺名称：<label class="w-[230px] max-3xl:w-[150px] text-left">{{ v.shopName || '——' }}</label>
          </div>

          <el-button
            v-if="formValues.stores.length > 1 && !readonly"
            link
            class="absolute right-[56px]"
            @click.prevent.stop="deleteRow(idx)"
          >
            删除
          </el-button>
        </template>

        <el-form
          :ref="($event) => handle($event, idx)"
          class="flex flex-col"
          require-asterisk-position="right"
          label-position="top"
          :disabled="readonly"
          :rules="rules"
          :model="v"
        >
          <div class="flex">
            <div class="w-[432px] mr-[114px]">
              <el-form-item
                prop="businessType"
                label="业务类型"
              >
                <el-select
                  v-model="v.businessType"
                  clearable
                  filterable
                  placeholder="请选择"
                  autocomplete="noy"
                  @keyup.enter="onKeyupEnter"
                >
                  <el-option
                    v-for="item in yibaoBussinessTypes"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
              <el-form-item prop="shopName" label="店铺名称">
                <el-input
                  v-model="v.shopName"
                  clearable
                  autocomplete="nony"
                  placeholder="请输入"
                  @keyup.enter="onKeyupEnter"
                />
              </el-form-item>
              <el-form-item prop="shopPicUrl" label="公域平台店铺截图">
                <card-image-upload
                  :action="UploadImageUrls.normal"
                  :image="v.shopPicUrl"
                  :readonly="hideRules"
                  @on-success="(res: any) => {
                    v.shopPicUrl = res.picUrl;
                    formRefs[idx]?.validateField?.('shopPicUrl');
                  }"
                />
              </el-form-item>
              <el-form-item prop="tradePicUrl" label="30天内资金流水截图">
                <card-image-upload
                  :action="UploadImageUrls.normal"
                  :image="v.tradePicUrl"
                  :readonly="hideRules"
                  @on-success="(res: any) => {
                    v.tradePicUrl = res.picUrl;
                    formRefs[idx]?.validateField?.('tradePicUrl');
                  }"
                />
              </el-form-item>
            </div>

            <div class="w-[432px]">
              <el-form-item prop="shopId" label="电商平台店铺ID">
                <el-input
                  v-model="v.shopId"
                  clearable
                  autocomplete="nony"
                  placeholder="请输入"
                  @keyup.enter="onKeyupEnter"
                />
              </el-form-item>
              <el-form-item prop="shopAddress" label="店铺地址">
                <el-input
                  v-model="v.shopAddress"
                  clearable
                  autocomplete="nony"
                  placeholder="请输入"
                  @keyup.enter="onKeyupEnter"
                />
              </el-form-item>
              <el-form-item prop="shopManagementPicUrl" label="公域管理后台截图">
                <card-image-upload
                  :action="UploadImageUrls.normal"
                  :image="v.shopManagementPicUrl"
                  :readonly="hideRules"
                  @on-success="(res: any) => {
                    v.shopManagementPicUrl = res.picUrl;
                    formRefs[idx]?.validateField?.('shopManagementPicUrl');
                  }"
                />
              </el-form-item>
            </div>
          </div>
        </el-form>
      </el-collapse-item>
    </template>
  </el-collapse>
</template>

<script setup lang="ts">
import { FormInstance, FormRules } from 'element-plus';
import { computed, PropType, ref, watch } from 'vue';
import { scrollToErrorField } from '@core/tools';
import { CardImageUpload, SvgIcon, onConfirm } from '@vue3/components';
import { UploadImageUrls } from '../../../../../api/common';
import { IYibaoShop, yibaoBussinessTypeName, yibaoBussinessTypes, EYibaoBusinessType } from '../../../../../types/yibao';

const emit = defineEmits<{'key-enter': []}>();

const props = defineProps({
  data: {
    type: Array as PropType<IYibaoShop[]>,
    default: () => {
      return [];
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  /** 自动滚动到错误位置 */
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  isShowTag: {
    type: Boolean,
    default: true,
  },
});

const formRefs = ref<FormInstance[]>([]);
const activeName = ref('0');

const formValues = ref({ stores: [] as IYibaoShop[] });
const selectedIdx = ref(0);

const hideRules = computed(() => props.readonly);
const rules = computed<FormRules<IYibaoShop>>(() => ({
  businessType: !hideRules.value
    ? [
      {
        required: true,
        message: '请选择业务类型',
      },
    ]
    : undefined,
  shopId: !hideRules.value
    ? [
      {
        required: true,
        message: '请输入电商平台店铺ID',
      },
    ]
    : undefined,
  shopName: !hideRules.value
    ? [
      {
        required: true,
        message: '请输入店铺名称',
      },
    ]
    : undefined,
  shopAddress: !hideRules.value
    ? [
      {
        required: true,
        message: '请输入店铺地址',
      },
    ]
    : undefined,
  shopPicUrl: !hideRules.value
    ? [
      {
        required: true,
        message: '请上传公域平台店铺截图',
      },
    ]
    : undefined,
  shopManagementPicUrl: !hideRules.value
    ? [
      {
        required: true,
        message: '请上传公域管理后台截图截图',
      },
    ]
    : undefined,
  tradePicUrl: !hideRules.value
    ? [
      {
        required: true,
        message: '请上传30天内资金流水截图',
      },
    ]
    : undefined,
}));

function deleteRow(index: number) {
  onConfirm({
    message: '是否确认当前操作',
    onOk: async () => {
      try {
        formRefs.value.splice(index, 1);
        formValues.value.stores.splice(index, 1);

        if (selectedIdx.value > index) selectedIdx.value--;

        if (selectedIdx.value === index) selectedIdx.value = 0;
      } catch (err) {
        console.log(err);
      }
    },
  });
}

function selectRow(index: number) {
  selectedIdx.value = index;
}

function handle(e: any, index: any) {
  // 保存下每个ref
  formRefs.value[index] = e;
}

async function validateAll() {
  let checks: boolean[] = [];

  for (const el of formRefs.value) {
    await el?.validate((valid: any) => {
      if (valid) {
        checks.push(true);
      } else {
        checks.push(false);
      }
    });
  }

  let idx = checks.findIndex((i) => i == false);

  if (idx > -1) {
    activeName.value = String(idx);

    setTimeout(() => {
      if (props.autoScrollToError) scrollToErrorField(formRefs.value[idx].$el);
    }, 330);
  }

  return idx;
}

async function validate() {
  const idx = await validateAll();

  return idx === -1;
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const idx = await validateAll();

  if (idx > -1) return;

  emit('key-enter');
}

function addStore() {
  formValues.value.stores.push({} as IYibaoShop);
}

watch(
  () => props.data,
  () => {
    const list = JSON.parse(JSON.stringify(props.data || [])) as IYibaoShop[];

    list.forEach((v, idx) => {
      if (v.selected) {
        selectedIdx.value = idx;
        activeName.value = String(idx);
      }
    });

    if (list.length === 0) {
      list.push({} as IYibaoShop);
    }

    formValues.value.stores = list;
  },
  { immediate: true },
);

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  getValues: () => {
    const d = JSON.parse(JSON.stringify(formValues.value.stores)) as IYibaoShop[];

    d.forEach((v, idx) => {
      v.selected = selectedIdx.value === idx;
    });

    return d as IYibaoShop[];
  },
});
</script>

<style scoped lang="scss" src="../../../open-account/index.scss" />

<style lang="scss" scoped>
.coll-title {
  @apply flex justify-start text-[#6B7891] cursor-pointer;

  label {
    display: inline-block;
    cursor: pointer;
    font-weight: 600;
    height: 36px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}

:deep(.file-icon) {
  @apply w-[16px] h-[16px];
}

:deep(.el-collapse-item__wrap) {
  @apply pt-5 pl-6;
}

:deep(.el-collapse-item.is-active) {
  .el-collapse-item__header {
    @apply bg-[#F5F6FA];

    .coll-title {
      label {
        font-weight: normal;
      }
    }
  }
}

:deep(.hauto .el-form-item__content) {
  line-height: 0;
}
</style>
